import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useToast } from "../lib/toast";
import type { Project, ProjectDefaults } from "@flowgen/shared";

interface Props {
  onOpen: (project: Project) => void;
}

export function ProjectGallery({ onOpen }: Props) {
  const toast = useToast();
  const [projects, setProjects] = useState<Project[] | null>(null);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Project | null>(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  async function reload() {
    const list = await api.get<Project[]>("/projects");
    setProjects(list);
  }

  useEffect(() => {
    reload().catch((err: any) => toast.show(err?.message ?? "Failed to load projects", "error"));
  }, []);

  async function create() {
    if (!name.trim()) return;
    setBusy(true);
    try {
      const p = await api.post<Project>("/projects", { name: name.trim() });
      setCreating(false);
      setName("");
      await reload();
      onOpen(p);
    } catch (err: any) {
      toast.show(err?.message ?? "Failed to create", "error");
    } finally {
      setBusy(false);
    }
  }

  async function remove(p: Project) {
    if (!confirm(`Delete project "${p.name}"? All characters, scenes, settings, edits, and media in it will be removed.`)) {
      return;
    }
    try {
      await api.delete(`/projects/${p.id}`);
      if (api.getActiveProjectId() === p.id) api.setActiveProjectId(null);
      await reload();
    } catch (err: any) {
      toast.show(err?.message ?? "Failed to delete", "error");
    }
  }

  return (
    <div>
      <div className="workspace-header">
        <div>
          <h2>Project Gallery</h2>
          <div className="sub">Create a project, then open it to work on characters, settings, scenes, and edits.</div>
        </div>
        <div style={{ marginLeft: "auto" }}>
          <button className="primary" onClick={() => setCreating(true)}>New project</button>
        </div>
      </div>

      {projects === null && <div className="muted">Loading...</div>}

      {projects && projects.length === 0 && (
        <div className="empty" style={{ minHeight: 160 }}>
          No projects yet. Create one to get started.
        </div>
      )}

      {projects && projects.length > 0 && (
        <div className="grid" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))" }}>
          {projects.map((p) => (
            <div key={p.id} className="card" style={{ padding: 16 }}>
              <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 6 }}>{p.name}</div>
              <div className="tiny muted" style={{ marginBottom: 8 }}>
                Updated {new Date(p.updatedAt).toLocaleString()}
              </div>
              {(p.defaults?.style || p.defaults?.camera || p.defaults?.aspectRatio) && (
                <div className="tiny muted" style={{ marginBottom: 12 }}>
                  Defaults: {[p.defaults.aspectRatio, p.defaults.style, p.defaults.camera]
                    .filter(Boolean)
                    .join(" · ")}
                </div>
              )}
              <div className="row between">
                <div className="row" style={{ gap: 4 }}>
                  <button className="danger" onClick={() => remove(p)}>Delete</button>
                  <button onClick={() => setEditing(p)}>Defaults</button>
                </div>
                <button className="primary" onClick={() => onOpen(p)}>Open</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {creating && (
        <div className="dialog-backdrop" onClick={() => !busy && setCreating(false)}>
          <div className="dialog" style={{ width: 400 }} onClick={(e) => e.stopPropagation()}>
            <h3>New project</h3>
            <div className="field">
              <label>Name</label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="My film"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter" && name.trim()) create();
                  if (e.key === "Escape") setCreating(false);
                }}
              />
            </div>
            <div className="row between">
              <button onClick={() => setCreating(false)} disabled={busy}>Cancel</button>
              <button className="primary" disabled={!name.trim() || busy} onClick={create}>
                {busy ? "Creating..." : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}

      {editing && (
        <ProjectDefaultsDialog
          project={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await reload();
            toast.show("Project defaults saved", "success");
          }}
        />
      )}
    </div>
  );
}

function ProjectDefaultsDialog({
  project,
  onClose,
  onSaved,
}: {
  project: Project;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [name, setName] = useState(project.name);
  const [defaults, setDefaults] = useState<ProjectDefaults>(project.defaults ?? {});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  async function save() {
    if (!name.trim()) {
      toast.show("Name is required", "error");
      return;
    }
    setBusy(true);
    try {
      const cleaned: ProjectDefaults = {
        aspectRatio: defaults.aspectRatio,
        style: defaults.style?.trim() || undefined,
        camera: defaults.camera?.trim() || undefined,
        motionIntensity: defaults.motionIntensity,
        styleBibleUrl: defaults.styleBibleUrl?.trim() || undefined,
      };
      await api.patch<Project>(`/projects/${project.id}`, {
        name: name.trim(),
        defaults: cleaned,
      });
      onSaved();
    } catch (err: any) {
      toast.show(err?.message ?? "Save failed", "error");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="dialog-backdrop" onClick={onClose}>
      <div className="dialog" style={{ width: 440 }} onClick={(e) => e.stopPropagation()}>
        <h3>Project defaults</h3>
        <div className="sub" style={{ marginBottom: 12 }}>
          Soft style/camera defaults for scenes that leave those fields empty.
        </div>
        <div className="field">
          <label>Name</label>
          <input value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="field-row">
          <div>
            <label>Aspect ratio</label>
            <select
              value={defaults.aspectRatio ?? ""}
              onChange={(e) =>
                setDefaults((d) => ({
                  ...d,
                  aspectRatio: (e.target.value || undefined) as ProjectDefaults["aspectRatio"],
                }))
              }
            >
              <option value="">None</option>
              <option value="16:9">16:9</option>
              <option value="9:16">9:16</option>
              <option value="1:1">1:1</option>
              <option value="4:3">4:3</option>
            </select>
          </div>
          <div>
            <label>Motion</label>
            <select
              value={defaults.motionIntensity ?? ""}
              onChange={(e) =>
                setDefaults((d) => ({
                  ...d,
                  motionIntensity: (e.target.value || undefined) as ProjectDefaults["motionIntensity"],
                }))
              }
            >
              <option value="">None</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </div>
        </div>
        <div className="field">
          <label>Style</label>
          <input
            value={defaults.style ?? ""}
            onChange={(e) => setDefaults((d) => ({ ...d, style: e.target.value || undefined }))}
            placeholder="e.g. cinematic, anamorphic"
          />
        </div>
        <div className="field">
          <label>Camera</label>
          <input
            value={defaults.camera ?? ""}
            onChange={(e) => setDefaults((d) => ({ ...d, camera: e.target.value || undefined }))}
            placeholder="e.g. dolly in, handheld"
          />
        </div>
        <div className="field">
          <label>Style bible URL</label>
          <input
            value={defaults.styleBibleUrl ?? ""}
            onChange={(e) =>
              setDefaults((d) => ({ ...d, styleBibleUrl: e.target.value || undefined }))
            }
            placeholder="/media/... or https://..."
          />
          <div className="tiny muted" style={{ marginTop: 4 }}>
            Soft moodboard ref injected into every scene prompt.
          </div>
        </div>
        <div className="row between" style={{ marginTop: 16 }}>
          <button onClick={onClose} disabled={busy}>Cancel</button>
          <button className="primary" disabled={busy} onClick={save}>
            {busy ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
